import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  PlusCircle,
  Edit2,
  Trash2,
  AlertTriangle,
  Check,
  X,
  DollarSign
} from 'lucide-react';
import DashboardPage from '../../components/DashboardPage';

// Mock data - In a real app, this would come from your API
const mockCategories = [
  {
    id: '1',
    name: 'Housing',
    budget: 2000,
    spent: 1800,
    color: '#68A511',
    description: 'Rent, utilities, and home maintenance',
    icon: '🏠'
  },
  {
    id: '2',
    name: 'Food',
    budget: 800,
    spent: 600,
    color: '#059669',
    description: 'Groceries and dining out',
    icon: '🍽️'
  },
  {
    id: '3',
    name: 'Transport',
    budget: 400,
    spent: 350,
    color: '#0284c7',
    description: 'Gas, public transit, and car maintenance',
    icon: '🚗'
  },
  {
    id: '4',
    name: 'Entertainment',
    budget: 300,
    spent: 200,
    color: '#7c3aed',
    description: 'Movies, events, and hobbies',
    icon: '🎮'
  },
  {
    id: '5',
    name: 'Utilities',
    budget: 250,
    spent: 200,
    color: '#db2777',
    description: 'Electricity, water, and internet',
    icon: '💡'
  },
  {
    id: '6',
    name: 'Shopping',
    budget: 400,
    spent: 100,
    color: '#dc2626',
    description: 'Clothing and personal items',
    icon: '🛍️'
  }
];

const BudgetCategories = () => {
  const [categories, setCategories] = useState(mockCategories);
  const [isLoading, setIsLoading] = useState(true);
  const [showAddCategory, setShowAddCategory] = useState(false);
  const [editingCategory, setEditingCategory] = useState<string | null>(null);
  const [newCategory, setNewCategory] = useState({
    name: '',
    budget: '',
    description: '',
    icon: '',
    color: '#68A511'
  });

  useEffect(() => {
    // Simulate API call
    const fetchData = async () => {
      setIsLoading(true);
      setTimeout(() => {
        setCategories(mockCategories);
        setIsLoading(false);
      }, 1000);
    };

    fetchData();
  }, []);

  const handleAddCategory = (e: React.FormEvent) => {
    e.preventDefault();
    // In a real app, this would be an API call
    const category = {
      id: Date.now().toString(),
      name: newCategory.name,
      budget: parseFloat(newCategory.budget),
      spent: 0,
      color: newCategory.color,
      description: newCategory.description,
      icon: newCategory.icon
    };
    setCategories([...categories, category]);
    setShowAddCategory(false);
    setNewCategory({ name: '', budget: '', description: '', icon: '', color: '#68A511' });
  };

  const handleUpdateCategory = (id: string, updates: any) => {
    setCategories(categories.map(cat => 
      cat.id === id ? { ...cat, ...updates } : cat
    ));
    setEditingCategory(null);
  };

  const handleDeleteCategory = (id: string) => {
    setCategories(categories.filter(cat => cat.id !== id));
  };

  return (
    <DashboardPage
      title="Budget Categories"
      subtitle="Manage your budget categories and spending limits"
    >
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="h-48 bg-gray-200 dark:bg-gray-700 rounded-xl" />
          ))}
        </div>
      ) : (
        <>
          {/* Add Category Button */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            onClick={() => setShowAddCategory(true)}
            className="mb-8 flex items-center gap-2 px-4 py-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e] transition-colors"
          >
            <PlusCircle className="w-5 h-5" />
            Add Category
          </motion.button>

          {/* Categories Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {categories.map((category) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm border border-gray-200 dark:border-gray-700"
              >
                {editingCategory === category.id ? (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleUpdateCategory(category.id, {
                        name: e.currentTarget.name.value,
                        budget: parseFloat(e.currentTarget.budget.value),
                        description: e.currentTarget.description.value
                      });
                    }}
                    className="space-y-4"
                  >
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name
                      </label>
                      <input
                        name="name"
                        defaultValue={category.name}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Budget
                      </label>
                      <input
                        name="budget"
                        type="number"
                        defaultValue={category.budget}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <input
                        name="description"
                        defaultValue={category.description}
                        className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      />
                    </div>
                    <div className="flex justify-end gap-2">
                      <button
                        type="button"
                        onClick={() => setEditingCategory(null)}
                        className="p-2 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                      >
                        <X className="w-5 h-5" />
                      </button>
                      <button
                        type="submit"
                        className="p-2 text-green-600 hover:text-green-700 dark:text-green-400 dark:hover:text-green-300"
                      >
                        <Check className="w-5 h-5" />
                      </button>
                    </div>
                  </form>
                ) : (
                  <>
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{category.icon}</span>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {category.name}
                        </h3>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditingCategory(category.id)}
                          className="p-1 text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.id)}
                          className="p-1 text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                      {category.description}
                    </p>
                    <div className="space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Budget</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          ${category.budget.toLocaleString()}
                        </span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600 dark:text-gray-400">Spent</span>
                        <span className="font-medium text-gray-900 dark:text-white">
                          ${category.spent.toLocaleString()}
                        </span>
                      </div>
                      <div className="w-full h-2 bg-gray-100 dark:bg-gray-700 rounded-full">
                        <div
                          className="h-2 rounded-full"
                          style={{
                            backgroundColor: category.color,
                            width: `${(category.spent / category.budget) * 100}%`
                          }}
                        />
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-500 dark:text-gray-400">
                          {((category.spent / category.budget) * 100).toFixed(1)}% used
                        </span>
                        <span className="text-gray-500 dark:text-gray-400">
                          ${(category.budget - category.spent).toLocaleString()} left
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            ))}
          </div>

          {/* Add Category Modal */}
          {showAddCategory && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
            >
              <motion.div
                initial={{ scale: 0.95 }}
                animate={{ scale: 1 }}
                className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full"
              >
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Add New Category
                </h3>
                <form onSubmit={handleAddCategory} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Name
                    </label>
                    <input
                      value={newCategory.name}
                      onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      placeholder="Category name"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Budget
                    </label>
                    <input
                      type="number"
                      value={newCategory.budget}
                      onChange={(e) => setNewCategory({ ...newCategory, budget: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      placeholder="Enter budget amount"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Description
                    </label>
                    <input
                      value={newCategory.description}
                      onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      placeholder="Category description"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Icon
                    </label>
                    <input
                      value={newCategory.icon}
                      onChange={(e) => setNewCategory({ ...newCategory, icon: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700"
                      placeholder="Enter emoji icon"
                    />
                  </div>
                  <div className="flex justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowAddCategory(false)}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-[#68A511] text-white rounded-lg hover:bg-[#527f0e]"
                    >
                      Add Category
                    </button>
                  </div>
                </form>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </DashboardPage>
  );
};

export default BudgetCategories; 